/**
 * <s-product-image-zoom> — PDP gallery: thumbnails, zoom preloading and magnify.
 *
 * Gallery buttons still open native dialogs declaratively via commandfor/show-modal.
 * This component adds:
 *   - `--show` command: a thumbnail scrolls the track to its slide; the active
 *     thumbnail follows whichever slide is in view (swipe or command).
 *   - Zoom preloading: warms the high-resolution image on hover/focus.
 *   - `--magnify` command and tap/click on the zoom image: scales the photo
 *     around the pointer so collectors can inspect corners and edges; dragging
 *     pans while magnified. Resets when the dialog closes.
 *
 * Required markup:
 *   [data-product-gallery]                 — horizontal scroll-snap track.
 *   [data-gallery-slide]                   — one slide per media, inside the track.
 *   [data-gallery-thumbs] button[data-gallery-index] — thumbnails (command="--show").
 *   [data-zoom-preload][commandfor]        — gallery trigger; commandfor points to a dialog id.
 *   dialog img[data-zoom-image]            — high-resolution image inside the target dialog.
 *   button[command="--magnify"][data-zoom-dialog] — magnify toggle inside the dialog.
 */
const MAGNIFY_SCALE = 2.5;

class SProductImageZoom extends HTMLElement {
  #ac;
  #observer;
  #preloaded = new Set();
  #preloadLinks = [];

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('pointerover', this.#handlePreloadIntent, opts);
    this.addEventListener('focusin', this.#handlePreloadIntent, opts);
    this.addEventListener('command', this.#handleCommand, opts);
    this.addEventListener('click', this.#handleImageClick, opts);
    this.addEventListener('pointermove', this.#handlePointerMove, opts);
    // `close` doesn't bubble; capture it from the zoom dialogs.
    this.addEventListener('close', this.#handleDialogClose, { ...opts, capture: true });

    this.#observeSlides();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();

    for (const link of this.#preloadLinks) {
      link.remove();
    }
    this.#preloadLinks = [];
    this.#preloaded.clear();
  }

  get #track() {
    return this.querySelector('[data-product-gallery]');
  }

  get #reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  #handleCommand = (event) => {
    if (event.command === '--show') {
      const index = Number(event.source?.dataset.galleryIndex);
      if (Number.isInteger(index)) this.#showSlide(index);
    } else if (event.command === '--magnify') {
      const dialog = document.getElementById(event.source?.dataset.zoomDialog || '');
      const image = dialog?.querySelector('img[data-zoom-image]');
      if (image instanceof HTMLImageElement) this.#toggleMagnify(image, null);
    }
  };

  #showSlide(index) {
    const track = this.#track;
    const slide = track?.querySelector(`[data-gallery-slide="${index}"]`);
    if (!track || !slide) return;

    // Rect delta works for both LTR and RTL scroll origins.
    const delta = slide.getBoundingClientRect().left - track.getBoundingClientRect().left;
    track.scrollBy({ left: delta, behavior: this.#reducedMotion ? 'auto' : 'smooth' });
    this.#setActiveThumb(index);
  }

  #observeSlides() {
    const track = this.#track;
    const slides = track?.querySelectorAll('[data-gallery-slide]');
    if (!track || !slides || slides.length < 2) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.#setActiveThumb(Number(entry.target.getAttribute('data-gallery-slide')));
          }
        }
      },
      { root: track, threshold: 0.6 }
    );
    for (const slide of slides) this.#observer.observe(slide);
  }

  #setActiveThumb(index) {
    const rail = this.querySelector('[data-gallery-thumbs]');
    if (!rail) return;

    for (const thumb of rail.querySelectorAll('button[data-gallery-index]')) {
      const active = Number(thumb.dataset.galleryIndex) === index;
      thumb.setAttribute('aria-current', active ? 'true' : 'false');
      if (active) this.#keepInRail(rail, thumb);
    }
  }

  // Scroll only the rail (never the page) so the active thumbnail stays visible.
  #keepInRail(rail, thumb) {
    const railRect = rail.getBoundingClientRect();
    const thumbRect = thumb.getBoundingClientRect();
    const behavior = this.#reducedMotion ? 'auto' : 'smooth';

    if (thumbRect.left < railRect.left) rail.scrollBy({ left: thumbRect.left - railRect.left, behavior });
    else if (thumbRect.right > railRect.right) rail.scrollBy({ left: thumbRect.right - railRect.right, behavior });
    if (thumbRect.top < railRect.top) rail.scrollBy({ top: thumbRect.top - railRect.top, behavior });
    else if (thumbRect.bottom > railRect.bottom) rail.scrollBy({ top: thumbRect.bottom - railRect.bottom, behavior });
  }

  #handleImageClick = (event) => {
    if (!(event.target instanceof HTMLImageElement) || !event.target.matches('[data-zoom-image]')) return;
    this.#toggleMagnify(event.target, event);
  };

  #toggleMagnify(image, pointer) {
    const magnified = !image.hasAttribute('data-magnified');

    if (magnified) {
      this.#setOrigin(image, pointer);
      image.setAttribute('data-magnified', '');
      image.style.transform = `scale(${MAGNIFY_SCALE})`;
    } else {
      this.#resetMagnify(image);
    }

    const dialog = image.closest('dialog');
    const toggle = dialog ? this.querySelector(`[data-zoom-dialog="${dialog.id}"]`) : null;
    toggle?.setAttribute('aria-pressed', magnified ? 'true' : 'false');
  }

  #handlePointerMove = (event) => {
    if (!(event.target instanceof HTMLImageElement) || !event.target.hasAttribute('data-magnified')) return;
    this.#setOrigin(event.target, event);
  };

  // Transform-origin follows the pointer, so the area under the finger or cursor is what's enlarged.
  #setOrigin(image, pointer) {
    if (!pointer) {
      image.style.transformOrigin = '50% 50%';
      return;
    }
    const rect = image.getBoundingClientRect();
    const x = Math.min(100, Math.max(0, ((pointer.clientX - rect.left) / rect.width) * 100));
    const y = Math.min(100, Math.max(0, ((pointer.clientY - rect.top) / rect.height) * 100));
    image.style.transformOrigin = `${x}% ${y}%`;
  }

  #resetMagnify(image) {
    image.removeAttribute('data-magnified');
    image.style.transform = '';
    image.style.transformOrigin = '';
  }

  #handleDialogClose = (event) => {
    if (!(event.target instanceof HTMLDialogElement)) return;

    const image = event.target.querySelector('img[data-zoom-image][data-magnified]');
    if (image instanceof HTMLImageElement) this.#resetMagnify(image);
    this.querySelector(`[data-zoom-dialog="${event.target.id}"]`)?.setAttribute('aria-pressed', 'false');
  };

  #handlePreloadIntent = (event) => {
    if (!(event.target instanceof Element)) return;

    const source = event.target.closest('[data-zoom-preload]');
    if (!source || !this.contains(source)) return;

    this.#preload(source);
  };

  #preload(source) {
    const dialogId = source.getAttribute('commandfor');
    if (!dialogId || this.#preloaded.has(dialogId)) return;

    const dialog = document.getElementById(dialogId);
    const image = dialog?.querySelector('img[data-zoom-image]');
    if (!(image instanceof HTMLImageElement)) return;

    this.#preloaded.add(dialogId);
    image.loading = 'eager';
    image.fetchPriority = 'high';

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = image.src;
    if (image.srcset) link.setAttribute('imagesrcset', image.srcset);
    if (image.sizes) link.setAttribute('imagesizes', image.sizes);
    document.head.append(link);
    this.#preloadLinks.push(link);

    if (typeof image.decode === 'function') {
      image.decode().catch(() => {});
    }
  }
}

if (!customElements.get('s-product-image-zoom')) {
  customElements.define('s-product-image-zoom', SProductImageZoom);
}
